import { KButton, KSpan } from "kahuna-base-react-components"
import React, { useState } from "react"
import { PaymentMethod } from "../../../../types"
import { createPaymentMethod, deletePaymentMethod, updatePaymentMethod } from "../../../../actions"
import { connect } from "react-redux"
import { lang } from "../../../../constants/languages"
import ConfirmationDialog from "../../../ui/modals/ConfirmationDialog"

const bankCodes = {
  "010": "ziraat.svg",
  "012": "halkbank.png",
  "013": "denizbank.svg",
  "015": "vakifbank.png",
  "046": "akbank.png",
  "062": "garanti.png",
  "064": "isbank.svg",
  "067": "yapikredi.png",
  "099": "ing.png",
  111: "qnb.png",
  123: "hsbc.png"
}

const getDisplayIdentifier = (paymentMethod: PaymentMethod): string => {
  const countryType = paymentMethod.countryType

  switch (countryType) {
    case "us": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Routing: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "ng":
      return `NUBAN: ${paymentMethod.accountNumber || "-"}`
    case "za": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Branch: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "au": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `BSB: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "ca": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Transit-Inst: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "in": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `IFSC: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "sg": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Bank-Branch: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "br": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Bank-Agency: ${paymentMethod.bankCode || "-"} | Acct: ****${last4}`
    }
    case "ph": {
      const last4 = (paymentMethod.accountNumber || "").slice(-4)
      return `Acct: ****${last4}`
    }
    case "nz": {
      const last4 = (paymentMethod.accountNumber || "").replace(/\D/g, "").slice(-4)
      return `${paymentMethod.bankName || "Bank"} | Acct: ****${last4}`
    }
    default:
      return paymentMethod.iban || "-"
  }
}

const ListComponent = ({
  paymentMethod,
  setEdit,
  paymentId,
  setEditableMethodKey,
  deletePaymentMethod,
  readOnly,
  isAdmin
}: {
  paymentMethod: PaymentMethod
  setEdit: React.Dispatch<React.SetStateAction<boolean>>
  paymentId: string
  setEditableMethodKey: React.Dispatch<React.SetStateAction<string>>
  deletePaymentMethod: (id: number) => void
  readOnly?: boolean
  isAdmin?: boolean
}) => {
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false)

  const handleConfirm = () => {
    if (paymentMethod.id !== undefined) {
      deletePaymentMethod(paymentMethod.id)
    }
  }

  const getIconName = (paymentMethod: PaymentMethod) => {
    const countryType = paymentMethod.countryType
    if (countryType && countryType !== "tr" && countryType !== "uk" && countryType !== "eu") {
      return "bank.svg"
    }
    const iban = paymentMethod.iban || ""
    const cleaned = iban.replace(/ /g, "")
    const bankName = Object.keys(bankCodes).find((key) => {
      return cleaned.substring(6, 9) === key.toString()
    })
    return bankName ? bankCodes[bankName] : "bank.svg"
  }

  return (
    <div className="w-[100%] flex flex-row justify-between items-center p-3 gap-2.5 table-row-payment-hover rounded-[10px] ">
      <div className="flex flex-row gap-3" style={{ width: "100%" }}>
        <div className="w-12 h-12 flex items-center justify-center">
          {getIconName(paymentMethod) && (
            <img
              className="object-contain max-h-full max-w-full"
              src={`/account_icons/bank-icons/${getIconName(paymentMethod)}`}
            />
          )}
        </div>
        <div className="flex flex-col gap-1" style={{ width: "100%" }}>
          <div className="flex justify-between" style={{ width: "100%" }}>
            <KSpan
              text={paymentMethod.bankName}
              fontSize={16}
              fontWeight={500}
              lineHeight="24px"
              letterSpacing="-0.176px"
              color="#000"
            />
            {isAdmin && (
              <div className="flex gap-2">
                <KSpan text="Swift:" />
                <KSpan text={paymentMethod.swiftCode || "-"} />
              </div>
            )}
          </div>
          <div className="flex justify-between" style={{ width: "100%" }}>
            <KSpan text={getDisplayIdentifier(paymentMethod)} color="#999" />
            {isAdmin && (
              <div className="flex gap-2">
                <KSpan text="Currency:" />
                <KSpan text={paymentMethod.currency || "-"} />
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <KSpan text="Account Holder Name:" />
              <KSpan text={paymentMethod.accountHolderName} />
            </div>
          )}
        </div>
      </div>
      <div className=" flex items-center justify-center p-0 rounded-[10px] gap-3">
        <div>
          {!readOnly && (
            <KButton
              dataTestId="edit-payment-method-button"
              text={lang.account.payment_methods.edit_button}
              height="32px"
              width="auto"
              padding="6px 12px"
              background="#FFF"
              onClick={() => {
                setEdit(true)
                setEditableMethodKey(paymentId)
              }}
            />
          )}
        </div>
        {!readOnly && (
          <div
            style={{
              border: "1px solid #FF5865",
              boxShadow: "0px 1px 2px 0px rgba(233, 53, 53, 0.08)",
              borderRadius: "10px"
            }}
          >
            <KButton
              dataTestId="delete-payment-method-button"
              leftIcon="/account_icons/delete-bin-red.svg"
              background="#FFF"
              padding="6px"
              width="32px"
              height="32px"
              borderRadius={10}
              onClick={() => {
                setOpenConfirmation(true)
              }}
            />
          </div>
        )}
      </div>
      <ConfirmationDialog
        loading={false}
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        handleConfirm={handleConfirm}
      />
    </div>
  )
}

export default connect(null, { createPaymentMethod, deletePaymentMethod, updatePaymentMethod })(ListComponent)
